// The quick search's query — what the header's search bar asks while the
// person types, and what Home's Basic search asks for its suggestions. One
// rule in one place, so the two lists can never disagree.
//
// classifyTerm picks the cheapest query for what was typed:
//   'key'  — the term IS a key, or the start of one: a project key exactly
//            ("YED2401"), or a key's prefix and a dash with digits or nothing
//            after it ("YED2401-", "YED2401-14", "YED-1"): search the
//            issue_key column ONLY (`key ~`, so a partial still surfaces its
//            siblings).
//   'text' — anything else: key + summary (the only path paying the per-row
//            EAV `text ~` cost).
// Only the key's SHAPE earns the shortcut. "WCM-1504-Comm" starts with a
// project key but is a summary's text, and a word that merely starts a
// project key ("Bar" beside BARX) is a word: both used to search keys only
// and came back empty. A summary part number that only LOOKS key-shaped
// ("FT200-0001") prefixes no project, so it still searches summaries.
// projectKeys is a Set of lower-cased keys, or null until it loads (→ the
// safe key+summary path).

export const QUICK_SEARCH_FIELDS = 'summary,status,issuetype,assignee,priority,project';
export const QUICK_SEARCH_DEBOUNCE_MS = 300;

export function escapeJql(str) {
  return String(str).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function classifyTerm(term, projectKeys) {
  if (!projectKeys || !projectKeys.size) return 'text';
  const t = String(term).trim().toLowerCase();
  if (projectKeys.has(t)) return 'key';
  const m = /^([a-z][a-z0-9_]*)-(\d*)$/.exec(t);
  if (!m) return 'text';
  for (const k of projectKeys) {
    if (k.startsWith(m[1])) return 'key';
  }
  return 'text';
}

// Key path: NO explicit ORDER BY — a broad prefix matches many rows and
// `ORDER BY updated` is a per-row EAV subquery; the engine's default order
// (newest first) needs none. The text path keeps updated-order.
export function quickSearchJql(term, projectKeys) {
  const esc = escapeJql(String(term).trim());
  return classifyTerm(term.trim(), projectKeys) === 'key'
    ? `key ~ "${esc}"`
    : `(key ~ "${esc}" OR text ~ "${esc}") ORDER BY updated DESC`;
}

// The project keys the classifier needs, lower-cased, from GET /api/projects.
export async function loadProjectKeySet(api) {
  const data = await api.getProjects();
  const values = Array.isArray(data) ? data : (data?.values || []);
  const set = new Set(values.map((p) => String(p?.key || '').toLowerCase()).filter(Boolean));
  return set.size ? set : null;
}
